import { CapitalSettings } from '@/lib/db/schema/capital'
import { DecisionSignal } from '@/types'

export interface HealthScoreResult {
  score: number
  status: string
  decisionSignal: DecisionSignal
  interpretation: string
}

export function calculateHealthScore(
  monthsData: any[], // actuals or simulation months (standardized format)
  capitalSettings: CapitalSettings | null
): HealthScoreResult {
  if (monthsData.length === 0) {
    return {
      score: 0,
      status: 'Unknown',
      decisionSignal: 'neutral',
      interpretation: 'Belum ada data untuk dianalisis.'
    }
  }

  // Use the latest month for current metrics
  const latestMonth = monthsData[monthsData.length - 1]
  const prevMonth = monthsData.length > 1 ? monthsData[monthsData.length - 2] : null

  let score = 0

  // 1. Profitability (Max 25)
  if (latestMonth.netProfit > 0) score += 15
  if (prevMonth && latestMonth.netProfit > prevMonth.netProfit) score += 10

  // 2. Liquidity (Max 20)
  const currentCash = latestMonth.cashBalance ?? latestMonth.endingCash ?? 0
  const prevCash = prevMonth ? (prevMonth.cashBalance ?? prevMonth.endingCash ?? 0) : 0
  if (currentCash > 0) score += 10
  if (prevMonth && currentCash > prevCash) score += 10

  // 3. Growth (Max 15)
  if (prevMonth && latestMonth.revenue > prevMonth.revenue) score += 15

  // 4. Efficiency (Max 15)
  const opexRatio = latestMonth.revenue > 0 ? (latestMonth.operatingExpense / latestMonth.revenue) : 1
  if (opexRatio < 0.4) score += 15
  else if (opexRatio < 0.7) score += 7

  // 5. Solvency (Max 10)
  score += 10 

  // 6. Capital Safety (Max 15)
  const currentEquity = latestMonth.endingEquity ?? latestMonth.netProfit
  const stopLoss = capitalSettings?.stopLossEquity || 0
  if (currentEquity > stopLoss * 1.5) score += 15
  else if (currentEquity > stopLoss) score += 5

  // Normalize
  score = Math.min(100, Math.max(0, score))

  let status = ''
  if (score > 80) status = 'Strong'
  else if (score > 60) status = 'Healthy'
  else if (score > 40) status = 'Warning'
  else if (score > 20) status = 'Critical'
  else status = 'Stop/Pivot'

  let decisionSignal: DecisionSignal = 'neutral'
  let interpretation = ''

  if (status === 'Strong' || status === 'Healthy') {
    decisionSignal = 'continue'
    interpretation = 'Bisnis sehat. Kas dan profitabilitas stabil. Anda dapat mempertimbangkan ekspansi terukur.'
  } else if (status === 'Warning') {
    if (latestMonth.revenue > 0 && latestMonth.netProfit < 0) {
      decisionSignal = 'restructure'
      interpretation = 'Warning: Ada penjualan, tetapi biaya operasional menggerus laba. Segera perbaiki struktur biaya!'
    } else {
      decisionSignal = 'improve'
      interpretation = 'Warning: Performa cenderung menurun. Fokus pada peningkatan margin dan efisiensi pengeluaran.'
    }
  } else {
    if (currentEquity <= stopLoss) {
      decisionSignal = 'close'
      interpretation = 'Kritis: Batas kerugian maksimal (Stop-loss) terlewati. Pertimbangkan opsi pivot ekstrem atau tutup.'
    } else if (latestMonth.revenue === 0) {
      decisionSignal = 'pivot'
      interpretation = 'Kritis: Sama sekali tidak ada arus pendapatan. Model bisnis ini tidak berhasil di pasar.'
    } else {
      decisionSignal = 'add_capital'
      interpretation = 'Kritis: Keuangan menipis, namun unit economics mungkin masih bisa diperbaiki. Butuh suntikan modal strategis.'
    }
  }

  return {
    score,
    status,
    decisionSignal,
    interpretation
  }
}
